import {
  addManagerQuestion,
  buildManagerQuestionAddPayload,
  fetchManagerQuestionList,
  unwrapManagerQuestionListPayload,
  updateManagerQuestion,
  type ManagerQuestionAddPayload,
  type QuestionDraftForApi,
  type QuestionScaleType
} from './managerQuestion'

export type SurveyQuestionTypeCode =
  | 0 // single
  | 1 // multiple
  | 2 // dropdown
  | 3 // text_short
  | 4 // text_long
  | 5 // matrix
  | 6 // scale
  | 7 // info

export type SurveyQuestionKind =
  | 'single'
  | 'multiple'
  | 'dropdown'
  | 'text_short'
  | 'text_long'
  | 'matrix'
  | 'scale'
  | 'info'

export const SURVEY_QUESTION_TYPE_CODE: Record<
  SurveyQuestionKind,
  SurveyQuestionTypeCode
> = {
  single: 0,
  multiple: 1,
  dropdown: 2,
  text_short: 3,
  text_long: 4,
  matrix: 5,
  scale: 6,
  info: 7
}

/** Backend ScaleType enum */
export const SURVEY_SCALE_TYPE = {
  NotSet: 0,
  OneToFive: 1,
  OneToTen: 2
} as const

export function surveyKindFromQuestionType(type: unknown): SurveyQuestionKind {
  const n =
    typeof type === 'number'
      ? type
      : typeof type === 'string' && type.trim()
        ? parseInt(type.trim(), 10)
        : NaN
  switch (n) {
    case 1:
      return 'multiple'
    case 2:
      return 'dropdown'
    case 3:
      return 'text_short'
    case 4:
      return 'text_long'
    case 5:
      return 'matrix'
    case 6:
      return 'scale'
    case 7:
      return 'info'
    default:
      return 'single'
  }
}

export function scaleTypeFromMax(scaleMax: number): QuestionScaleType {
  return scaleMax >= 10
    ? SURVEY_SCALE_TYPE.OneToTen
    : SURVEY_SCALE_TYPE.OneToFive
}

export function scaleMaxFromScaleType(scaleType: unknown): number {
  const n =
    typeof scaleType === 'number'
      ? scaleType
      : typeof scaleType === 'string' && scaleType.trim()
        ? parseInt(scaleType.trim(), 10)
        : NaN
  if (n === SURVEY_SCALE_TYPE.OneToTen) {
    return 10
  }
  if (n === SURVEY_SCALE_TYPE.OneToFive) {
    return 5
  }
  return 5
}

export function pickScaleTypeFromQuestion(
  question: Record<string, unknown> | null | undefined
): QuestionScaleType | null {
  if (!question) {
    return null
  }
  const raw = question.scaleType ?? question.ScaleType
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim()
        ? parseInt(raw.trim(), 10)
        : NaN
  if (
    n === SURVEY_SCALE_TYPE.NotSet ||
    n === SURVEY_SCALE_TYPE.OneToFive ||
    n === SURVEY_SCALE_TYPE.OneToTen
  ) {
    return n
  }
  return null
}

export function buildScaleColsFromScaleType(scaleType: unknown): string[] {
  const max = scaleMaxFromScaleType(scaleType)
  return Array.from({ length: max }, (_, i) => String(i + 1))
}

export type SurveyQuestionDraftForApi = QuestionDraftForApi & {
  kind: SurveyQuestionKind
  isRequired?: boolean
  scaleMin?: number
  scaleMax?: number
  scaleMinLabel?: string
  scaleMaxLabel?: string
  matrixRows?: string[]
  matrixCols?: string[]
}

export type SurveyQuestionMeta = {
  isRequired?: boolean
  scaleMinLabel?: string
  scaleMaxLabel?: string
  matrixCols?: string[]
}

const SURVEY_META_PREFIX = '__survey_meta__:'

export function encodeSurveyMeta(meta: SurveyQuestionMeta): string {
  return `${SURVEY_META_PREFIX}${JSON.stringify(meta)}`
}

export function decodeSurveyMeta(raw: unknown): SurveyQuestionMeta | null {
  const text = String(raw ?? '').trim()
  if (!text.startsWith(SURVEY_META_PREFIX)) {
    return null
  }
  try {
    const parsed = JSON.parse(text.slice(SURVEY_META_PREFIX.length))
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    return parsed as SurveyQuestionMeta
  } catch {
    return null
  }
}

function applySurveyType(
  payload: ManagerQuestionAddPayload,
  kind: SurveyQuestionKind
): ManagerQuestionAddPayload {
  return {
    ...payload,
    type: SURVEY_QUESTION_TYPE_CODE[kind] as ManagerQuestionAddPayload['type']
  }
}

export function buildManagerQuestionnaireQuestionPayload(
  draft: SurveyQuestionDraftForApi,
  testItemId: string,
  position: number
): ManagerQuestionAddPayload {
  const apiDraft: QuestionDraftForApi = {
    id: draft.id,
    text: draft.text,
    answerMode: draft.kind === 'multiple' ? 'multiple' : 'single',
    options: draft.options ?? [],
    explanation: '',
    questionFileId: draft.questionFileId,
    questionImageIsNew: draft.questionImageIsNew
  }

  if (
    draft.kind === 'info' ||
    draft.kind === 'text_short' ||
    draft.kind === 'text_long' ||
    draft.kind === 'scale'
  ) {
    // API expects exactly one answerOption with empty fields.
    apiDraft.options = [{ text: '', correct: false }]
  }

  if (draft.kind === 'matrix') {
    const rows = (draft.matrixRows ?? []).filter((r) => String(r).trim())
    apiDraft.options = rows.map((row) => ({
      text: row,
      correct: false
    }))
  }

  if (
    draft.kind === 'single' ||
    draft.kind === 'multiple' ||
    draft.kind === 'dropdown'
  ) {
    apiDraft.options = (draft.options ?? []).map((o) => ({
      ...o,
      correct: false
    }))
  }

  let payload = applySurveyType(
    buildManagerQuestionAddPayload(apiDraft, testItemId, position),
    draft.kind
  )

  if (draft.kind === 'scale' || draft.kind === 'matrix') {
    payload = {
      ...payload,
      scaleType: scaleTypeFromMax(draft.scaleMax ?? 5)
    }
  } else {
    payload = {
      ...payload,
      scaleType: SURVEY_SCALE_TYPE.NotSet
    }
  }

  return payload
}

export {
  addManagerQuestion as addManagerQuestionnaireQuestion,
  updateManagerQuestion as updateManagerQuestionnaireQuestion,
  fetchManagerQuestionList as fetchManagerQuestionnaireQuestionList,
  unwrapManagerQuestionListPayload as unwrapManagerQuestionnaireQuestionListPayload
}
import {
  addManagerQuestion,
  buildManagerQuestionAddPayload,
  fetchManagerQuestionList,
  unwrapManagerQuestionListPayload,
  updateManagerQuestion,
  type ManagerQuestionAddPayload,
  type QuestionDraftForApi,
  type QuestionScaleType
} from './managerQuestion'

export type SurveyQuestionTypeCode =
  | 0 // single
  | 1 // multiple
  | 2 // dropdown
  | 3 // text_short
  | 4 // text_long
  | 5 // matrix
  | 6 // scale
  | 7 // info

export type SurveyQuestionKind =
  | 'single'
  | 'multiple'
  | 'dropdown'
  | 'text_short'
  | 'text_long'
  | 'matrix'
  | 'scale'
  | 'info'

export const SURVEY_QUESTION_TYPE_CODE: Record<
  SurveyQuestionKind,
  SurveyQuestionTypeCode
> = {
  single: 0,
  multiple: 1,
  dropdown: 2,
  text_short: 3,
  text_long: 4,
  matrix: 5,
  scale: 6,
  info: 7
}

/** Backend ScaleType enum */
export const SURVEY_SCALE_TYPE = {
  NotSet: 0,
  OneToFive: 1,
  OneToTen: 2
} as const

export function surveyKindFromQuestionType(type: unknown): SurveyQuestionKind {
  const n =
    typeof type === 'number'
      ? type
      : typeof type === 'string' && type.trim()
        ? parseInt(type.trim(), 10)
        : NaN
  switch (n) {
    case 1:
      return 'multiple'
    case 2:
      return 'dropdown'
    case 3:
      return 'text_short'
    case 4:
      return 'text_long'
    case 5:
      return 'matrix'
    case 6:
      return 'scale'
    case 7:
      return 'info'
    default:
      return 'single'
  }
}

export function scaleTypeFromMax(scaleMax: number): QuestionScaleType {
  return scaleMax >= 10
    ? SURVEY_SCALE_TYPE.OneToTen
    : SURVEY_SCALE_TYPE.OneToFive
}

export function scaleMaxFromScaleType(scaleType: unknown): number {
  const n =
    typeof scaleType === 'number'
      ? scaleType
      : typeof scaleType === 'string' && scaleType.trim()
        ? parseInt(scaleType.trim(), 10)
        : NaN
  if (n === SURVEY_SCALE_TYPE.OneToTen) {
    return 10
  }
  if (n === SURVEY_SCALE_TYPE.OneToFive) {
    return 5
  }
  return 5
}

export function pickScaleTypeFromQuestion(
  question: Record<string, unknown> | null | undefined
): QuestionScaleType | null {
  if (!question) {
    return null
  }
  const raw = question.scaleType ?? question.ScaleType
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim()
        ? parseInt(raw.trim(), 10)
        : NaN
  if (
    n === SURVEY_SCALE_TYPE.NotSet ||
    n === SURVEY_SCALE_TYPE.OneToFive ||
    n === SURVEY_SCALE_TYPE.OneToTen
  ) {
    return n
  }
  return null
}

export function buildScaleColsFromScaleType(scaleType: unknown): string[] {
  const max = scaleMaxFromScaleType(scaleType)
  return Array.from({ length: max }, (_, i) => String(i + 1))
}

export type SurveyQuestionDraftForApi = QuestionDraftForApi & {
  kind: SurveyQuestionKind
  isRequired?: boolean
  scaleMin?: number
  scaleMax?: number
  scaleMinLabel?: string
  scaleMaxLabel?: string
  matrixRows?: string[]
  matrixCols?: string[]
}

export type SurveyQuestionMeta = {
  isRequired?: boolean
  scaleMinLabel?: string
  scaleMaxLabel?: string
  matrixCols?: string[]
}

const SURVEY_META_PREFIX = '__survey_meta__:'

export function encodeSurveyMeta(meta: SurveyQuestionMeta): string {
  return `${SURVEY_META_PREFIX}${JSON.stringify(meta)}`
}

export function decodeSurveyMeta(raw: unknown): SurveyQuestionMeta | null {
  const text = String(raw ?? '').trim()
  if (!text.startsWith(SURVEY_META_PREFIX)) {
    return null
  }
  try {
    const parsed = JSON.parse(text.slice(SURVEY_META_PREFIX.length))
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    return parsed as SurveyQuestionMeta
  } catch {
    return null
  }
}

function applySurveyType(
  payload: ManagerQuestionAddPayload,
  kind: SurveyQuestionKind
): ManagerQuestionAddPayload {
  return {
    ...payload,
    type: SURVEY_QUESTION_TYPE_CODE[kind] as ManagerQuestionAddPayload['type']
  }
}

export function buildManagerQuestionnaireQuestionPayload(
  draft: SurveyQuestionDraftForApi,
  testItemId: string,
  position: number
): ManagerQuestionAddPayload {
  const apiDraft: QuestionDraftForApi = {
    id: draft.id,
    text: draft.text,
    answerMode: draft.kind === 'multiple' ? 'multiple' : 'single',
    options: draft.options ?? [],
    explanation: '',
    questionFileId: draft.questionFileId,
    questionImageIsNew: draft.questionImageIsNew
  }

  if (
    draft.kind === 'info' ||
    draft.kind === 'text_short' ||
    draft.kind === 'text_long' ||
    draft.kind === 'scale'
  ) {
    // API expects exactly one answerOption with empty fields.
    apiDraft.options = [{ text: '', correct: false }]
  }

  if (draft.kind === 'matrix') {
    const rows = (draft.matrixRows ?? []).filter((r) => String(r).trim())
    apiDraft.options = rows.map((row) => ({
      text: row,
      correct: false
    }))
  }

  if (
    draft.kind === 'single' ||
    draft.kind === 'multiple' ||
    draft.kind === 'dropdown'
  ) {
    apiDraft.options = (draft.options ?? []).map((o) => ({
      ...o,
      correct: false
    }))
  }

  let payload = applySurveyType(
    buildManagerQuestionAddPayload(apiDraft, testItemId, position),
    draft.kind
  )

  if (draft.kind === 'scale' || draft.kind === 'matrix') {
    payload = {
      ...payload,
      scaleType: scaleTypeFromMax(draft.scaleMax ?? 5)
    }
  } else {
    payload = {
      ...payload,
      scaleType: SURVEY_SCALE_TYPE.NotSet
    }
  }

  return payload
}

export {
  addManagerQuestion as addManagerQuestionnaireQuestion,
  updateManagerQuestion as updateManagerQuestionnaireQuestion,
  fetchManagerQuestionList as fetchManagerQuestionnaireQuestionList,
  unwrapManagerQuestionListPayload as unwrapManagerQuestionnaireQuestionListPayload
}
